import { useTranslation } from "react-i18next"

import type { DocumentNameLookup, ExtractionItemLookup, OpenSourceFn } from "./extraction-citation"
import type { DeliverableAnalysis, RequirementAnalysis, RoleAnalysis } from "../../types"

import { Skeleton } from "@/components/ui/skeleton"

import {
  DeliverableItemRow,
  RequirementItemRow,
  RoleItemRow,
  StatusSummary,
} from "./bid-review-compliance-items"

// ── Types ─────────────────────────────────────────────────────────────────────

type ComplianceTabProps = {
  requirementAnalyses?: RequirementAnalysis[]
  roleAnalyses?: RoleAnalysis[]
  deliverableAnalyses?: DeliverableAnalysis[]
  extractionLookup?: ExtractionItemLookup
  docNameLookup?: DocumentNameLookup
  onOpenSource?: OpenSourceFn
  isLoading: boolean
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-4/5" />
        </div>
      ))}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function ComplianceTab({
  requirementAnalyses,
  roleAnalyses,
  deliverableAnalyses,
  extractionLookup,
  docNameLookup,
  onOpenSource,
  isLoading,
}: ComplianceTabProps) {
  const { t } = useTranslation()
  if (isLoading) {
    return <LoadingSkeleton />
  }

  const hasRequirements = (requirementAnalyses?.length ?? 0) > 0
  const hasRoles = (roleAnalyses?.length ?? 0) > 0
  const hasDeliverables = (deliverableAnalyses?.length ?? 0) > 0

  if (!hasRequirements && !hasRoles && !hasDeliverables) {
    return (
      <p className="text-muted-foreground py-8 text-center text-[13px]">
        {t("tender.bidReview.complianceTab.empty")}
      </p>
    )
  }

  const requirementsByCategory: Record<string, RequirementAnalysis[]> = {}
  if (hasRequirements) {
    for (const item of requirementAnalyses!) {
      const cat = item.category ?? t("tender.bidReview.complianceTab.defaultCategory")
      const group = requirementsByCategory[cat] as RequirementAnalysis[] | undefined
      if (!group) {
        requirementsByCategory[cat] = [item]
      } else {
        group.push(item)
      }
    }
  }

  const categoryEntries = Object.entries(requirementsByCategory)
  const hasMultipleCategories = categoryEntries.length > 1

  return (
    <div className="flex flex-col gap-6">
      {hasRequirements && (
        <section>
          <div className="mb-2 flex items-center gap-3">
            <h4 className="text-[12px] font-semibold tracking-wide uppercase">
              {t("tender.bidReview.complianceTab.sectionRequirements")}
            </h4>
            <StatusSummary items={requirementAnalyses!} />
          </div>
          <div className="flex flex-col gap-4">
            {hasMultipleCategories
              ? categoryEntries.map(([category, items]) => (
                  <div key={category}>
                    <div className="mb-1.5 flex items-center gap-2">
                      <span className="text-muted-foreground text-[11px] font-medium">
                        {category}
                      </span>
                      <StatusSummary items={items} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {items.map((item) => (
                        <RequirementItemRow
                          key={item.requirementId}
                          bidQuote={item.bidQuote}
                          category={item.category}
                          confidence={item.confidence}
                          description={item.description}
                          docNameLookup={docNameLookup}
                          extractionLookup={extractionLookup}
                          gapDescription={item.gapDescription}
                          id={item.requirementId}
                          isMandatory={item.isMandatory}
                          reasoning={item.reasoning}
                          sourceDocument={item.sourceDocument}
                          sourcePage={item.sourcePage}
                          status={item.status}
                          suggestedFix={item.suggestedFix}
                          supportingSnippets={item.supportingSnippets}
                          onOpenSource={onOpenSource}
                        />
                      ))}
                    </div>
                  </div>
                ))
              : requirementAnalyses!.map((item) => (
                  <RequirementItemRow
                    key={item.requirementId}
                    bidQuote={item.bidQuote}
                    category={item.category}
                    confidence={item.confidence}
                    description={item.description}
                    docNameLookup={docNameLookup}
                    extractionLookup={extractionLookup}
                    gapDescription={item.gapDescription}
                    id={item.requirementId}
                    isMandatory={item.isMandatory}
                    reasoning={item.reasoning}
                    sourceDocument={item.sourceDocument}
                    sourcePage={item.sourcePage}
                    status={item.status}
                    suggestedFix={item.suggestedFix}
                    supportingSnippets={item.supportingSnippets}
                    onOpenSource={onOpenSource}
                  />
                ))}
          </div>
        </section>
      )}

      {hasRoles && (
        <section>
          <div className="mb-2 flex items-center gap-3">
            <h4 className="text-[12px] font-semibold tracking-wide uppercase">
              {t("tender.bidReview.complianceTab.sectionRoles")}
            </h4>
            <StatusSummary items={roleAnalyses!} />
          </div>
          <div className="flex flex-col gap-1.5">
            {roleAnalyses!.map((item) => (
              <RoleItemRow
                key={item.roleId}
                bidQuote={item.bidQuote}
                confidence={item.confidence}
                docNameLookup={docNameLookup}
                extractionLookup={extractionLookup}
                gapDescription={item.gapDescription}
                id={item.roleId}
                quantityRequired={item.quantityRequired}
                reasoning={item.reasoning}
                roleName={item.roleName}
                status={item.status}
                suggestedFix={item.suggestedFix}
                supportingSnippets={item.supportingSnippets}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </section>
      )}

      {hasDeliverables && (
        <section>
          <div className="mb-2 flex items-center gap-3">
            <h4 className="text-[12px] font-semibold tracking-wide uppercase">
              {t("tender.bidReview.complianceTab.sectionDeliverables")}
            </h4>
            <StatusSummary items={deliverableAnalyses!} />
          </div>
          <div className="flex flex-col gap-1.5">
            {deliverableAnalyses!.map((item) => (
              <DeliverableItemRow
                key={item.deliverableId}
                bidQuote={item.bidQuote}
                confidence={item.confidence}
                deliverableName={item.deliverableName}
                docNameLookup={docNameLookup}
                extractionLookup={extractionLookup}
                gapDescription={item.gapDescription}
                id={item.deliverableId}
                isRequired={item.isRequired}
                reasoning={item.reasoning}
                status={item.status}
                suggestedFix={item.suggestedFix}
                supportingSnippets={item.supportingSnippets}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
